import React, { useEffect, useMemo, useState } from 'react'
import { BASE_URL_Upload } from '../Component/commonComponents/ApiEndpoints'
import { useSidebar } from '../Component/SidebarContext'

const REGISTRY_TITLE = 'Migration Certificate'

const formatDdMmYyyy = (value) => {
  if (value === null || value === undefined || value === '') return ''
  if (typeof value === 'string' && /^\d{1,2}-\d{1,2}-\d{4}$/.test(value.trim())) {
    return value.trim()
  }
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) {
    return typeof value === 'string' ? value : ''
  }
  const day = String(d.getDate()).padStart(2, '0')
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const year = d.getFullYear()
  return `${day}-${month}-${year}`
}

const safeStr = (v, fallback = '') => {
  if (v === null || v === undefined) return fallback
  const s = String(v).trim()
  if (!s || s.toLowerCase() === 'null' || s.toLowerCase() === 'undefined') return fallback
  return s
}

const resolveMediaUrl = (src) => {
  const t = safeStr(src, '')
  if (!t) return ''
  if (/^https?:\/\//i.test(t)) return t
  if (t.startsWith('//')) return `https:${t}`
  const base = String(BASE_URL_Upload || '').replace(/\/$/, '')
  const path = t.startsWith('/') ? t : `/${t}`
  return base ? `${base}${path}` : t
}

const displayTitleFromProps = (certificateTitle) => {
  const t = safeStr(certificateTitle, '')
  if (!t || t === REGISTRY_TITLE) return 'MIGRATION CERTIFICATE'
  return t.toUpperCase()
}

const CertificateTemplate6 = ({
  studentName = 'Student Name',
  courseName = 'Course Name',
  instituteName = 'Institute Name',
  date = '',
  certificateTitle = 'Migration Certificate',
  certificateId = '',
  logoUrl = '',
  authorityName = 'Authorized Signatory',
  student: studentProp,
  course: courseProp,
  certificate: certificateProp,
  institute: instituteProp,
  selectedStudent,
  selectedCourse,
  suppressSidebarOffset = false,
}) => {
  const certificate = certificateProp || {}
  const student = studentProp || selectedStudent || {}
  const course = courseProp || selectedCourse || {}
  const institute = instituteProp || {}

  const { isCollapsed } = useSidebar()
  const [marginLeft, setMarginLeft] = useState('0px')
  const [photoFailed, setPhotoFailed] = useState(false)
  const [fallbackPhotoFailed, setFallbackPhotoFailed] = useState(false)

  useEffect(() => {
    if (suppressSidebarOffset) return undefined
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245
        const collapsedWidth = 85
        const gap = 1
        setMarginLeft(
          isCollapsed ? `${collapsedWidth + gap}px` : `${expandedWidth + gap}px`,
        )
      } else {
        setMarginLeft('0px')
      }
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed, suppressSidebarOffset])

  useEffect(() => {
    setPhotoFailed(false)
    setFallbackPhotoFailed(false)
  }, [student?.student_image, student?.photo, student?.image])

  useEffect(() => {
    const root = document.documentElement
    const onBeforePrint = () => root.classList.add('certificate-template-6-print-session')
    const onAfterPrint = () => root.classList.remove('certificate-template-6-print-session')
    window.addEventListener('beforeprint', onBeforePrint)
    window.addEventListener('afterprint', onAfterPrint)
    return () => {
      window.removeEventListener('beforeprint', onBeforePrint)
      window.removeEventListener('afterprint', onAfterPrint)
      root.classList.remove('certificate-template-6-print-session')
    }
  }, [])

  const data = useMemo(() => {
    const instName =
      safeStr(institute.institute_name) ||
      safeStr(institute.name) ||
      safeStr(instituteName)

    const studName = safeStr(student.name) || safeStr(studentName)
    const father = safeStr(student.father_name)
    const mother = safeStr(student.mother_name)
    const admission = safeStr(student.admission_number)

    const crsName =
      safeStr(course.course_name) || safeStr(course.name) || safeStr(courseName)
    const durationRaw = course.course_duration ?? course.duration
    const duration = (() => {
      if (durationRaw === null || durationRaw === undefined) return ''
      const s = String(durationRaw).trim()
      if (!s) return ''
      return /month/i.test(s) ? s : `${s} months`
    })()

    const start = formatDdMmYyyy(course.start_date ?? course.startDate)
    const end = formatDdMmYyyy(course.end_date ?? course.endDate)

    const certNum =
      safeStr(certificate.certificate_number) ||
      safeStr(certificate.certificate_id) ||
      safeStr(certificate.id != null ? String(certificate.id) : '') ||
      safeStr(certificateId)

    const issueRaw =
      certificate.issue_date ?? certificate.date ?? certificate.issueDate ?? date
    const issue = formatDdMmYyyy(issueRaw) || (typeof issueRaw === 'string' ? issueRaw : '')

    const migrationReason = safeStr(
      certificate.migration_reason ?? certificate.reason ?? student.migration_reason,
      '',
    )
    const noDues = safeStr(certificate.no_dues_note ?? certificate.no_dues_status, '')
    const docNo = safeStr(
      certificate.document_number ?? certificate.register_entry_no ?? certificate.register_no,
      '',
    )

    const conductLine =
      safeStr(certificate.conduct_statement, '') ||
      'The conduct of the student during their stay was satisfactory.'

    const imgRaw =
      student.student_image ?? student.photo ?? student.image ?? student.profile_image
    const photoSrc = resolveMediaUrl(imgRaw)
    const titlePrinted = displayTitleFromProps(certificateTitle)

    return {
      instName,
      studName,
      father,
      mother,
      admission,
      crsName,
      duration,
      start,
      end,
      certNum,
      issue,
      migrationReason,
      noDues,
      docNo,
      conductLine,
      photoSrc,
      titlePrinted,
    }
  }, [
    certificate,
    student,
    course,
    institute,
    studentName,
    courseName,
    instituteName,
    certificateId,
    certificateTitle,
    date,
  ])

  const {
    instName,
    studName,
    father,
    mother,
    admission,
    crsName,
    duration,
    start,
    end,
    certNum,
    issue,
    migrationReason,
    noDues,
    docNo,
    conductLine,
    photoSrc,
    titlePrinted,
  } = data

  const studyPeriod =
    start && end ? `${start} to ${end}` : start || end ? `${start || '—'} to ${end || '—'}` : ''

  const defaultPhoto = `${process.env.PUBLIC_URL || ''}/studentprofile.jpg`
  const showPhoto = photoSrc && !photoFailed
  const logoResolved = resolveMediaUrl(logoUrl) || logoUrl
  const layoutMarginLeft = suppressSidebarOffset ? '0px' : marginLeft

  const handlePrint = () => {
    window.print()
  }

  return (
    <div
      className="certificate-template-6-page"
      style={{
        marginLeft: layoutMarginLeft,
        transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
        willChange: 'margin-left',
        WebkitTransition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        zIndex: 1,
      }}
    >
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Merriweather:wght@700&family=Source+Sans+3:wght@400;500;600;700&display=swap');

          /*
           * Print: same pattern as CertificateTemplate4 — fixed A4 landscape stage, no
           * transform/absolute (Chrome print often breaks them). @page margin insets the sheet;
           * the card keeps 297:210 using height = content height and width = height * 297/210.
           */
          .certificate-template-6-page {
            min-height: 100vh;
            background: #edf1f5;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 20px;
            box-sizing: border-box;
          }

          .certificate-template-6-wrap {
            width: min(297mm, 100%);
            box-sizing: border-box;
          }

          .certificate-template-6-print-stage {
            display: block;
            width: 100%;
          }

          .certificate-template-6-actions {
            display: flex;
            justify-content: flex-end;
            margin-bottom: 12px;
          }

          .certificate-template-6-print-btn {
            border: 1px solid #1e3a8a;
            background: #fff;
            color: #1e3a8a;
            padding: 9px 16px;
            border-radius: 6px;
            font-weight: 700;
            font-family: 'Source Sans 3', sans-serif;
            font-size: 13px;
            cursor: pointer;
          }

          .certificate-template-6-card {
            width: 100%;
            aspect-ratio: 297 / 210;
            max-height: min(90vh, 210mm);
            background: #fff;
            border: 1px solid #64748b;
            box-shadow: 0 10px 28px rgba(15, 23, 42, 0.1);
            position: relative;
            overflow: hidden;
          }

          .certificate-template-6-card::before {
            content: '';
            position: absolute;
            inset: 12px;
            border: 1px solid #1e3a8a;
            pointer-events: none;
          }

          .certificate-template-6-watermark {
            position: absolute;
            inset: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            font-family: 'Merriweather', serif;
            font-size: clamp(48px, 15vw, 120px);
            color: rgba(30, 58, 138, 0.05);
            letter-spacing: 0.14em;
            text-transform: uppercase;
            pointer-events: none;
          }

          .certificate-template-6-inner {
            position: relative;
            z-index: 1;
            height: 100%;
            box-sizing: border-box;
            padding: clamp(18px, 3.5vw, 28px);
            display: flex;
            flex-direction: column;
            min-height: 0;
          }

          .certificate-template-6-header {
            text-align: center;
            position: relative;
            flex-shrink: 0;
            min-height: 92px;
          }

          .certificate-template-6-logo {
            width: 56px;
            height: 56px;
            margin: 0 auto 8px;
            object-fit: contain;
            object-position: center;
            border: 1px solid #64748b;
            border-radius: 4px;
            background: #fff;
            flex-shrink: 0;
          }

          .certificate-template-6-logo-ph {
            width: 56px;
            height: 56px;
            margin: 0 auto 8px;
            border: 1px dashed #94a3b8;
            border-radius: 4px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 9px;
            color: #94a3b8;
            font-family: 'Source Sans 3', sans-serif;
          }

          .certificate-template-6-photo {
            position: absolute;
            top: 0;
            right: 0;
            width: 72px;
            height: 88px;
            border: 1px solid #64748b;
            background: #f8fafc;
            overflow: hidden;
          }

          .certificate-template-6-photo img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }

          .certificate-template-6-photo-ph {
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 9px;
            color: #94a3b8;
            text-align: center;
            padding: 4px;
          }

          .certificate-template-6-institute {
            margin: 0;
            font-family: 'Source Sans 3', sans-serif;
            font-size: clamp(13px, 1.9vw, 16px);
            font-weight: 700;
            letter-spacing: 0.05em;
            text-transform: uppercase;
            color: #1e3a8a;
          }

          .certificate-template-6-title {
            margin: 10px 0 0;
            font-family: 'Merriweather', serif;
            font-size: clamp(22px, 3.2vw, 29px);
            font-weight: 700;
            color: #0f172a;
            letter-spacing: 0.08em;
            text-transform: uppercase;
            text-decoration: underline;
            text-underline-offset: 5px;
            text-decoration-thickness: 1px;
          }

          .certificate-template-6-body {
            margin-top: 16px;
            font-family: 'Source Sans 3', sans-serif;
            font-size: clamp(12px, 1.35vw, 14px);
            line-height: 1.75;
            color: #334155;
            text-align: left;
          }

          .certificate-template-6-body p {
            margin: 0 0 12px;
          }

          .certificate-template-6-name {
            font-weight: 700;
            color: #0f172a;
            font-size: clamp(13px, 1.45vw, 15px);
          }

          .certificate-template-6-em {
            font-weight: 600;
            color: #1e293b;
          }

          .certificate-template-6-section-line {
            height: 1px;
            background: #cbd5e1;
            margin: 7px 0 8px;
            flex-shrink: 0;
          }

          .certificate-template-6-details {
            display: grid;
            grid-template-columns: repeat(3, minmax(0, 1fr));
            gap: 6px 8px;
          }

          .certificate-template-6-detail {
            border: 1px solid #e2e8f0;
            border-radius: 4px;
            padding: 8px;
            background: #f8fafc;
          }

          .certificate-template-6-label {
            font-size: 8px;
            text-transform: uppercase;
            letter-spacing: 0.06em;
            color: #64748b;
            font-weight: 700;
          }

          .certificate-template-6-value {
            font-size: 10px;
            color: #0f172a;
            font-weight: 600;
            margin-top: 2px;
            word-break: break-word;
          }

          .certificate-template-6-meta {
            margin-top: 8px;
            display: flex;
            flex-wrap: wrap;
            gap: 10px 18px;
            font-size: 10px;
            font-family: 'Source Sans 3', sans-serif;
            color: #475569;
          }

          .certificate-template-6-meta strong {
            color: #0f172a;
            font-weight: 700;
          }

          .certificate-template-6-note {
            margin-top: 6px;
            font-size: 9px;
            color: #64748b;
            font-family: 'Source Sans 3', sans-serif;
          }

          .certificate-template-6-bottom {
            margin-top: auto;
            display: flex;
            justify-content: center;
            align-items: flex-end;
            gap: 10px;
            padding-top: 8px;
            flex-shrink: 0;
          }

          .certificate-template-6-signatures {
            display: grid;
            grid-template-columns: repeat(3, minmax(0, 1fr));
            gap: 8px 10px;
            width: 100%;
          }

          .certificate-template-6-sign {
            text-align: center;
            font-family: 'Source Sans 3', sans-serif;
          }

          .certificate-template-6-seal {
            width: 44px;
            height: 44px;
            margin: 0 auto 4px;
            border: 2px solid #475569;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 8px;
            color: #475569;
            text-transform: uppercase;
            background: #f8fafc;
          }

          .certificate-template-6-sign-line {
            border-top: 1px solid #0f172a;
            margin-top: 24px;
            padding-top: 5px;
            font-size: 8px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            color: #0f172a;
          }

          .certificate-template-6-sign-sub {
            font-size: 8px;
            color: #64748b;
            margin-top: 2px;
            font-weight: 500;
            text-transform: none;
          }

          @media (max-width: 620px) {
            .certificate-template-6-photo {
              position: relative;
              margin: 0 auto 8px;
            }
            .certificate-template-6-header {
              min-height: unset;
            }
            .certificate-template-6-details {
              grid-template-columns: 1fr;
            }
            .certificate-template-6-bottom {
              flex-direction: column;
              align-items: center;
            }
            .certificate-template-6-signatures {
              width: 100%;
              grid-template-columns: 1fr;
            }
          }

          @media print {
            /*
             * TemplateBasedCertificate: avoid fixed/visibility hacks (see CertificateTemplate4).
             * Sidenav: hide every App child except the main content column.
             */
            @page {
              size: A4 landscape;
              margin: 5mm;
            }

            html,
            body,
            #root {
              margin: 0 !important;
              padding: 0 !important;
              width: 100% !important;
              max-width: none !important;
              background: #fff !important;
              height: auto !important;
              min-height: 0 !important;
              max-height: none !important;
              overflow: visible !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              color-adjust: exact !important;
            }

            .App:has(.certificate-template-6-page) > *:not(:last-child),
            html.certificate-template-6-print-session .App > *:not(:last-child) {
              display: none !important;
            }

            .App:has(.certificate-template-6-page) > *:last-child,
            html.certificate-template-6-print-session .App > *:last-child {
              width: 100% !important;
              max-width: 100% !important;
              flex: 1 1 auto !important;
              min-width: 0 !important;
              padding: 0 !important;
              margin: 0 !important;
            }

            .template-based-certificate-root {
              margin: 0 !important;
              padding: 0 !important;
              width: 100% !important;
              max-width: none !important;
              min-width: 0 !important;
              box-sizing: border-box !important;
            }

            .template-tc-print-preview-wrap {
              padding: 0 !important;
              margin: 0 !important;
              width: 100% !important;
              max-width: none !important;
              box-sizing: border-box !important;
              break-inside: avoid;
              page-break-inside: avoid;
            }

            .template-preview-shell,
            .template-preview-surface,
            .template-preview-surface > div {
              overflow: visible !important;
              max-height: none !important;
              max-width: none !important;
              width: 100% !important;
              margin-left: 0 !important;
              margin-right: 0 !important;
              height: auto !important;
              break-inside: avoid;
              page-break-inside: avoid;
            }

            .certificate-template-6-page {
              position: relative !important;
              left: auto !important;
              top: auto !important;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: flex-start;
              box-sizing: border-box;
              width: 100% !important;
              max-width: none !important;
              min-height: 0 !important;
              max-height: none !important;
              height: auto !important;
              padding: 0 !important;
              margin: 0 !important;
              margin-left: 0 !important;
              background: #fff !important;
              overflow: visible !important;
              break-inside: avoid;
              page-break-inside: avoid;
              break-after: avoid;
              page-break-after: avoid;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }

            .certificate-template-6-page,
            .certificate-template-6-page * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }

            .certificate-template-6-wrap {
              width: 100%;
              max-width: none;
              margin: 0;
              flex-shrink: 0;
              break-inside: avoid;
              page-break-inside: avoid;
            }

            .certificate-template-6-actions,
            .certificate-template-6-actions * {
              display: none !important;
            }

            /*
             * Content box after 5mm @page margins: 287mm × 200mm. Card keeps 297:210 using
             * width = 200 * 297/210 (fits inside 287); centered, no non-uniform stretch.
             */
            .certificate-template-6-print-stage {
              position: relative !important;
              display: flex !important;
              flex-direction: row !important;
              justify-content: center !important;
              align-items: center !important;
              width: 100% !important;
              min-width: 0 !important;
              height: 200mm !important;
              min-height: 200mm !important;
              max-height: 200mm !important;
              max-width: 287mm !important;
              margin: 0 auto !important;
              margin-top: 0 !important;
              padding: 0 !important;
              box-sizing: border-box !important;
              flex-shrink: 0;
              break-inside: avoid !important;
              page-break-inside: avoid !important;
              overflow: hidden !important;
              transform: none !important;
            }

            .certificate-template-6-card {
              position: relative !important;
              width: calc(200mm * 297 / 210) !important;
              height: 200mm !important;
              min-height: 0 !important;
              max-width: min(287mm, 100%) !important;
              aspect-ratio: unset !important;
              display: flex !important;
              flex-direction: column !important;
              box-shadow: none !important;
              border-radius: 0 !important;
              break-inside: avoid !important;
              page-break-inside: avoid !important;
              overflow: hidden !important;
              flex: 0 0 auto !important;
              box-sizing: border-box !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }

            .certificate-template-6-card::before {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              inset: 8px !important;
            }

            .certificate-template-6-watermark {
              font-size: 48px !important;
            }

            .certificate-template-6-inner {
              flex: 1 1 auto !important;
              min-height: 0 !important;
              height: auto !important;
              max-height: none !important;
              display: flex !important;
              flex-direction: column !important;
              justify-content: space-between !important;
              box-sizing: border-box !important;
              // padding: 8px 11px 7px !important;
              overflow: hidden !important;
            }

            .certificate-template-6-header {
              flex-shrink: 0 !important;
              min-height: 0 !important;
            }

            .certificate-template-6-logo,
            .certificate-template-6-logo-ph {
              width: 50px !important;
              height: 50px !important;
              object-fit: contain !important;
              object-position: center !important;
              margin: 0 auto 4px !important;
            }

            .certificate-template-6-institute {
              font-size: 14px !important;
            }

            .certificate-template-6-title {
              margin: 2px 0 0 !important;
              font-size: 19px !important;
              text-underline-offset: 3px !important;
            }

            .certificate-template-6-body {
              flex: 0 1 auto !important;
              min-height: 0 !important;
              margin-top: 2px !important;
              font-size: 14px !important;
              line-height: 1.5 !important;
            }

            .certificate-template-6-body p {
              margin: 0 0 4px !important;
            }

            .certificate-template-6-name {
              font-size: 15.5px !important;
            }

            .certificate-template-6-em {
              font-size: 14px !important;
            }

            .certificate-template-6-bottom {
              margin-top: 0 !important;
              padding-top: 2px !important;
              flex-shrink: 0 !important;
            }

            .certificate-template-6-sign-line {
              margin-top: 6px !important;
              font-size: 8px !important;
            }

            .certificate-template-6-sign-sub {
              font-size: 7.5px !important;
            }

            .certificate-template-6-seal {
              width: 38px !important;
              height: 38px !important;
              font-size: 7.5px !important;
            }

            .certificate-template-6-section-line {
              margin: 3px 0 4px !important;
            }

            .certificate-template-6-details {
              gap: 3px 4px !important;
            }

            .certificate-template-6-detail {
              padding: 4px 5px !important;
            }

            .certificate-template-6-label {
              font-size: 8.5px !important;
            }

            .certificate-template-6-value {
              font-size: 12px !important;
            }

            .certificate-template-6-meta {
              margin-top: 2px !important;
              gap: 2px 8px !important;
              font-size: 10.75px !important;
            }

            .certificate-template-6-note {
              font-size: 9.5px !important;
            }

            .certificate-template-6-photo {
              width: 60px !important;
              height: 74px !important;
            }

            .certificate-template-6-signatures {
              gap: 6px 8px !important;
            }

            .certificate-template-6-print-stage,
            .certificate-template-6-card {
              page-break-before: avoid !important;
              break-before: avoid-page !important;
              page-break-after: avoid !important;
              break-after: avoid-page !important;
            }
          }
        `}
      </style>

      <div className="certificate-template-6-wrap">
        <div className="certificate-template-6-actions">
          <button
            type="button"
            className="certificate-template-6-print-btn"
            onClick={handlePrint}
          >
            Print / Download
          </button>
        </div>

        <div className="certificate-template-6-print-stage">
          <article className="certificate-template-6-card">
          <div className="certificate-template-6-watermark" aria-hidden>
            Academic
          </div>
          <div className="certificate-template-6-inner">
            <header className="certificate-template-6-header">
              {logoResolved ? (
                <img src={logoResolved} alt="" className="certificate-template-6-logo" />
              ) : (
                <div className="certificate-template-6-logo-ph">Logo</div>
              )}
              <div className="certificate-template-6-photo" aria-label="Student photograph">
                {showPhoto ? (
                  <img src={photoSrc} alt="" onError={() => setPhotoFailed(true)} />
                ) : !fallbackPhotoFailed ? (
                  <img
                    src={defaultPhoto}
                    alt=""
                    onError={() => setFallbackPhotoFailed(true)}
                  />
                ) : (
                  <div className="certificate-template-6-photo-ph">Photo</div>
                )}
              </div>
              <p className="certificate-template-6-institute">{instName || 'Institute Name'}</p>
              <h1 className="certificate-template-6-title">{titlePrinted}</h1>
            </header>

            <section className="certificate-template-6-body">
              <p>
                This is to certify that <span className="certificate-template-6-name">{studName || '—'}</span>
                {father || mother ? (
                  <>
                    ,<br />
                    {father ? (
                      <>
                        S/o <span className="certificate-template-6-em">{father}</span>
                        {mother ? ' and ' : ''}
                      </>
                    ) : null}
                    {mother ? (
                      <>
                        M/o <span className="certificate-template-6-em">{mother}</span>
                      </>
                    ) : null}
                    ,
                  </>
                ) : admission ? (
                  ','
                ) : null}
                {admission ? (
                  <>
                    <br />
                    bearing Admission No. <span className="certificate-template-6-em">{admission}</span>,
                  </>
                ) : null}
                <br />
                was a bonafide student of this institution.
              </p>

              <p>
                The student has completed the course{' '}
                <span className="certificate-template-6-em">{crsName || '—'}</span> during the period
                from <span className="certificate-template-6-em">{start || '—'}</span> to{' '}
                <span className="certificate-template-6-em">{end || '—'}</span>.
              </p>

              <p>
                The student is hereby permitted to migrate to another institution. We wish them success
                in their future academic pursuits.
              </p>

              <p>{conductLine}</p>

              <div className="certificate-template-6-section-line" />

              <div className="certificate-template-6-details">
                <div className="certificate-template-6-detail">
                  <div className="certificate-template-6-label">Course name</div>
                  <div className="certificate-template-6-value">{crsName || '—'}</div>
                </div>
                <div className="certificate-template-6-detail">
                  <div className="certificate-template-6-label">Duration</div>
                  <div className="certificate-template-6-value">{duration || '—'}</div>
                </div>
                <div className="certificate-template-6-detail">
                  <div className="certificate-template-6-label">Study period</div>
                  <div className="certificate-template-6-value">{studyPeriod || '—'}</div>
                </div>
              </div>

              <div className="certificate-template-6-meta">
                <span>
                  <strong>Certificate No:</strong> {certNum || '—'}
                </span>
                <span>
                  <strong>Issue Date:</strong> {issue || '—'}
                </span>
                {docNo ? (
                  <span>
                    <strong>Document No:</strong> {docNo}
                  </span>
                ) : null}
                {migrationReason ? (
                  <span>
                    <strong>Migration Reason:</strong> {migrationReason}
                  </span>
                ) : null}
              </div>
              {noDues ? <div className="certificate-template-6-note">No Dues: {noDues}</div> : null}
            </section>

            <footer className="certificate-template-6-bottom">
              <div className="certificate-template-6-signatures">
                <div className="certificate-template-6-sign">
                  <div className="certificate-template-6-sign-line">Principal / Head</div>
                  <div className="certificate-template-6-sign-sub">Head of Institution</div>
                </div>
                <div className="certificate-template-6-sign">
                  <div className="certificate-template-6-sign-line">{authorityName}</div>
                  <div className="certificate-template-6-sign-sub">Authorized Signatory</div>
                </div>
                <div className="certificate-template-6-sign">
                  <div className="certificate-template-6-seal">Seal</div>
                  <div className="certificate-template-6-sign-line">Office Seal</div>
                  <div className="certificate-template-6-sign-sub">Mandatory Placement</div>
                </div>
              </div>
            </footer>
          </div>
        </article>
        </div>
      </div>
    </div>
  )
}

export default CertificateTemplate6
